import React, { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

// @ts-ignore - untyped JS module from coupon-common
import { actions as affiliateActions } from "coupon-common/src/modules/affiliateBusiness";
// @ts-ignore - untyped JS module from coupon-common
import { actions as couponActions } from "coupon-common/src/modules/customerCoupons/all";

import { Footer } from "../../components/Footer";
import { HeaderWrapper } from "../../components/Header/HeaderWrapper";
import { SectionDotNav } from "../../components/Header/Sectiondotnav";
import { AppDownloadBannerSection } from "../../components/Page1/AppDownloadBannerSection";
import { AppDownloadBannerSectionSM } from "../../components/Page1/AppDownloadBannerSectionSM";
import { Box } from "../../components/Page1/Box";
import DealsCategoryBannerSection, { DealsShowcaseSection } from "../../components/Page1/DealsCategoryBannerSection";
import { FAQSection } from "../../components/Page1/FAQSection";
import { HeroSection } from "../../components/Page1/HeroSection";
import { PremiumMembershipBanner } from "../../components/Page1/PremiumMembershipBanner";
import { PremiumMembershipBannerSM } from "../../components/Page1/PremiumMembershipBannerSM";
import { RewardsProgramSection } from "../../components/Page1/RewardsProgramSection";
import SaveRedeemSection from "../../components/Page1/SaveRedeemSection";
import SmartDealsBannerSection from "../../components/Page1/SmartDealsBannerSection";
import { AuthorizedRedirect } from "../../..";

// Same fallback used by GuestHome for the '/' guest experience — this
// marketing page has no location picker, so it just needs *a* city to
// pull real coupons/brands for, and Halifax is the app's existing default.
const DEFAULT_CITY_ID = 347;

const Page1: React.FC = () => {
  const dispatch = useDispatch();

  // Real partner logos for the "Smarter Deals Start Here" brand strip —
  // same affiliate business list the guest home page and Online Brands
  // page read from, so this shows actual top partners, not stock logos.
  const affiliateState = useSelector((s: any) => s.affiliateBusiness || {}, shallowEqual);
  const affiliateBusinesses = (affiliateState.data && affiliateState.data.results) || [];

  // Real coupon data for the "Coupons for Everyone" cards row — same
  // customerCoupons/all module used by the Home/GuestHome feeds.
  const couponsState = useSelector((s: any) => s.customerCoupons.all || {}, shallowEqual);
  const { items: couponItems, result: couponResult } = couponsState;
  const couponResults = (couponResult && couponResult.results) || [];

  useEffect(() => {
    if (!affiliateState.fetching && !affiliateState.fetched) {
      dispatch(affiliateActions.getAffiliateBusinesses());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    dispatch(couponActions.getAllList({ page: 1, city: DEFAULT_CITY_ID }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const topBrands = useMemo(() => {
    const seen = new Set();
    const logos: any[] = [];
    affiliateBusinesses.forEach((business: any) => {
      if (!business.logo || seen.has(business.logo)) return;
      seen.add(business.logo);
      logos.push(business);
    });
    return logos.slice(0, 12);
  }, [affiliateBusinesses]);

  const featuredCoupons = useMemo(() => {
    if (!couponResults.length || !couponItems) return [];
    return couponResults
      .map((id: string | number) => couponItems[id])
      .filter(Boolean)
      .slice(0, 8);
  }, [couponResults, couponItems]);

  return (
    <div className="w-full bg-transparent overflow-x-hidden min-h-screen">
      <AuthorizedRedirect isCustomer={true} />
      <HeaderWrapper />
      <SectionDotNav />

      <main className="relative z-10 flex flex-col items-center pt-[140px]">
        
        <div className="absolute inset-0 -z-10" />

        <section id="hero" className="relative w-full flex justify-center z-20">
          {/* Same container as every section below; the hero has no card of
              its own, so its content is inset by the cards' inner padding
              (lg:px-[80px]) to line up with the text inside them. */}
          <div className="w-full max-w-[1700px] px-6">
            <div className="lg:px-[80px]">
              <HeroSection />
            </div>
          </div>
        </section>

        <section id="smart-deals" className="relative w-full flex justify-center z-10">
          <div className="w-full max-w-[1700px] px-6">
            <SmartDealsBannerSection brands={topBrands} />
          </div>
        </section>

        <section id="categories" className="w-full flex justify-center mt-24">
          <div className="w-full max-w-[1700px] px-6">
            <DealsCategoryBannerSection coupons={featuredCoupons} />
          </div>
        </section>

        <section id="personalised" className="w-full flex justify-center mt-24">
          <div className="w-full max-w-[1700px] px-6">
            <Box />
          </div>
        </section>

        <section id="save-redeem" className="w-full flex justify-center mt-24">
          <div className="w-full max-w-[1700px] px-6">
            <SaveRedeemSection />
          </div>
        </section>

        <section id="rewards" className="w-full flex justify-center">
          <div className="w-full max-w-[1700px] px-6">
            <RewardsProgramSection />
          </div>
        </section>

        <section id="premium" className="w-full flex justify-center">
          <div className="w-full max-w-[1700px] px-6 hidden lg:block">
            <PremiumMembershipBanner />
          </div>
          <div className="w-full max-w-[1700px] px-6 lg:hidden">
            <PremiumMembershipBannerSM />
          </div>
        </section>

        <section id="discover" className="w-full flex justify-center mt-24">
          <div className="w-full max-w-[1700px] px-6">
            <DealsShowcaseSection />
          </div>
        </section>

        <section id="faq" className="w-full flex justify-center mt-24">
          <div className="w-full max-w-[1700px] px-6">
            <FAQSection containerClassName="w-full" />
          </div>
        </section>

        <section id="download" className="w-full flex justify-center mb-12 mt-24">
          <div className="w-full max-w-[1700px] px-6 hidden lg:block">
            <AppDownloadBannerSection />
          </div>
          <div className="w-full max-w-[1700px] px-6 lg:hidden">
            <AppDownloadBannerSectionSM />
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default Page1;